import GuideLayout from '@/pages/shared/GuideLayout';

export default function GuideDependency() {
  return (
    <GuideLayout
      title="プロジェクト依存関係とは？管理方法とガントチャートでの表現｜”おもいむすび”"
      description="プロジェクト管理におけるタスクの依存関係（FS/SS/FF/SF）とその管理方法を解説。ガントチャートでの矢印表示、クリティカルパスへの影響、AIによる自動設定について。"
      keywords="プロジェクト管理 依存関係,プロジェクト 依存関係,タスク 依存関係,ガントチャート 依存関係,先行タスク 後続タスク"
      canonical="https://omoimusubi.asia/guides/project-dependency/"
      h1="プロジェクト依存関係とは？管理方法と活用法"
      subtitle="タスク間の依存関係の種類と管理方法、ガントチャートでの視覚化について解説します。"
      relatedFeatures={[
        { label: 'AIガントチャート', path: '/features/ai-gantt/' },
        { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
      ]}
      relatedGuides={[
        { label: 'クリティカルパスとは', path: '/guides/critical-path/' },
        { label: 'ガントチャートとは', path: '/guides/gantt-chart/' },
        { label: 'プロジェクトスケジュール管理', path: '/guides/project-schedule-management/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20Japanese%20office%20scene%20a%20Japanese%20male%20project%20manager%20in%20his%2030s%20looking%20at%20a%20large%20whiteboard%20displaying%20a%20complex%20task%20dependency%20flowchart%20with%20arrows%20connecting%20task%20boxes%20of%20different%20colors%20he%20is%20tracing%20the%20dependency%20arrows%20with%20his%20finger%20showing%20the%20workflow%20logic%20natural%20daylight%20through%20large%20windows%20indoor%20plants%20refreshing%20analytical%20Japanese%20corporate%20atmosphere&width=1200&height=600&seq=guide-dependency-01&orientation=landscape"
          alt="プロジェクト依存関係 - タスク間の依存関係を図示するプロジェクトマネージャー"
          className="w-full h-auto"
        />
      </div>
      <section>
        <h2>タスクの依存関係とは</h2>
        <p>タスクの依存関係とは、あるタスクを開始または完了するために、別のタスクがどのような状態である必要があるかを定義する関係です。依存関係を正しく設定することで、プロジェクトのスケジュールが現実的になり、遅延の影響範囲も正確に把握できます。</p>
      </section>

      <section>
        <h2>依存関係の4つの種類</h2>
        <p>プロジェクト管理では、以下の4種類の依存関係が使われます。最も一般的なのはFS（Finish-to-Start）です。</p>
        <ul>
          <li><strong>FS（Finish-to-Start）：終了-開始</strong><br/>先行タスクが完了したら後続タスクを開始できる。例：「設計書作成が完了したら、プログラミングを開始する」</li>
          <li><strong>SS（Start-to-Start）：開始-開始</strong><br/>先行タスクが開始したら後続タスクも開始できる。例：「フロントエンド開発が始まったら、同時にバックエンド開発も開始する」</li>
          <li><strong>FF（Finish-to-Finish）：終了-終了</strong><br/>先行タスクが完了したら後続タスクも完了する（同時に完了させる必要がある）。例：「テスト実施が完了したら、テスト結果報告書も同時に完了させる」</li>
          <li><strong>SF（Start-to-Finish）：開始-終了</strong><br/>先行タスクが開始したら後続タスクを完了できる。最も珍しいタイプ。例：「新システムの稼働が開始されたら、旧システムの運用を終了する」</li>
        </ul>
      </section>

      <section>
        <h2>依存関係管理の重要性</h2>
        <p>依存関係を適切に管理することで、以下のようなメリットがあります。</p>
        <ul>
          <li><strong>スケジュールの精度向上</strong>：タスクの前後関係を正しく反映することで、現実的なスケジュールを作成できます。</li>
          <li><strong>遅延の影響範囲が明確に</strong>：あるタスクが遅れた場合、どの後続タスクに影響が出るかが即座に分かります。</li>
          <li><strong>クリティカルパスの特定</strong>：依存関係をもとに、プロジェクトの最短完了日数を決める経路（クリティカルパス）を特定できます。</li>
          <li><strong>リソースの最適配分</strong>：並行して進められるタスクと、順番にしか進められないタスクを区別できます。</li>
        </ul>
      </section>

      <section>
        <h2>依存関係設定時の注意点</h2>
        <ul>
          <li>必要以上に依存関係を設定しすぎない。過剰な依存関係はスケジュールの柔軟性を奪います。</li>
          <li>外部（別チームや取引先）への依存は特に注意して管理する。コントロールできない要素はリスクとして認識しましょう。</li>
          <li>依存関係を見直すタイミングを定期的に設ける。プロジェクトの進行に伴い、依存関係が変わることがあります。</li>
        </ul>
      </section>

      <section>
        <h2>AIによる依存関係の自動提案</h2>
        <p>”おもいむすび”では、AIが資料の内容を解析し、タスク間の依存関係を自動で提案します。企画書やWBSに記載された作業の順序関係をAIが読み取り、適切な依存タイプ（FS/SS/FF/SF）でガントチャート上に矢印として視覚化します。ユーザーはAIの提案を確認し、必要に応じて修正するだけで、正確な依存関係を設定できます。</p>
      </section>
    </GuideLayout>
  );
}